import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import { ArrowLeft, User, Mail, Phone, Building2, FileText, CreditCard, LogOut, Save, Loader2, Crown, Calendar, Download, AlertCircle } from 'lucide-react';
import api from '../services/api';
import { Toaster, toast } from 'sonner';

const VerhuurderAccountPage = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [navigatingToPremium, setNavigatingToPremium] = useState(false);

  // Handle navigation to premium with loading animation
  const handleNavigateToPremium = async () => {
    setNavigatingToPremium(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    navigate('/premium');
  };
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    description: '',
    kvkNumber: '',
    vatNumber: ''
  });

  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        companyName: user.companyName || '',
        description: user.description || '',
        kvkNumber: user.kvkNumber || '',
        vatNumber: user.vatNumber || ''
      });
      setSubscription(user.subscription || null);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'invoices') {
      loadInvoices();
    }
  }, [activeTab]);

  const loadInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const response = await api.get('/user/invoices');
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Kon facturen niet laden');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/user/profile', {
        name: profile.name,
        phone: profile.phone,
        companyName: profile.companyName,
        description: profile.description,
        kvkNumber: profile.kvkNumber,
        vatNumber: profile.vatNumber
      });
      toast.success('Profiel bijgewerkt!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Kon profiel niet opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Weet je zeker dat je je abonnement wilt beëindigen?')) return;
    
    try {
      await api.post('/subscription/cancel');
      toast.success('Abonnement wordt beëindigd aan het einde van de huidige periode');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Kon abonnement niet beëindigen');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('nl-NL', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      timeZone: 'Europe/Amsterdam'
    });
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount / 100);
  };

  // Loading overlay for premium navigation
  if (navigatingToPremium) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#1F3A5F] to-[#0D2137] flex items-center justify-center z-50">
        <div className="text-center">
          <div className="mb-6 animate-pulse">
            <SwipeMatchLogo size="large" variant="white" className="justify-center" />
          </div>
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.15s'}}></div>
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.3s'}}></div>
            </div>
            <p className="text-white/80 text-sm font-medium">Premium opties laden...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F7F7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E7D32]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/verhuurder')} className="p-2 hover:bg-[#F7F7F7] rounded-full">
            <ArrowLeft className="w-5 h-5 text-[#2A2A2A]" />
          </button>
          <h1 className="text-xl font-bold text-[#2A2A2A]">Mijn Account</h1>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'profile', label: 'Profiel', icon: User },
              { id: 'subscription', label: 'Abonnement', icon: Crown },
              { id: 'invoices', label: 'Facturen', icon: FileText }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id 
                    ? 'border-[#2E7D32] text-[#2E7D32]' 
                    : 'border-transparent text-[#9E9E9E] hover:text-[#2A2A2A]'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="font-medium text-sm">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Account Info Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#2E7D32]" />
                Accountgegevens
              </h2>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-sm font-medium text-[#666] mb-1 block">Naam</label>
                  <Input
                    value={profile.name}
                    onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))}
                    placeholder="Je naam"
                    className="h-12"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#666] mb-1 block">Email</label>
                  <Input
                    value={profile.email}
                    disabled
                    className="h-12 bg-[#F7F7F7]"
                  />
                  <p className="text-xs text-[#9E9E9E] mt-1">Email kan niet worden gewijzigd</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#666] mb-1 block">Telefoon</label>
                  <Input
                    value={profile.phone}
                    onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+31 6 12345678"
                    className="h-12"
                  />
                </div>
              </div>
            </div>

            {/* Business Info Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#2E7D32]" />
                Bedrijfsgegevens
              </h2>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-[#666] mb-1 block">Bedrijfsnaam</label>
                  <Input
                    value={profile.companyName}
                    onChange={(e) => setProfile(p => ({ ...p, companyName: e.target.value }))}
                    placeholder="Naam van je bedrijf (optioneel)"
                    className="h-12"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#666] mb-1 block">KVK-nummer</label>
                  <Input
                    value={profile.kvkNumber}
                    onChange={(e) => setProfile(p => ({ ...p, kvkNumber: e.target.value }))}
                    placeholder="12345678"
                    className="h-12"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#666] mb-1 block">BTW-nummer</label>
                  <Input
                    value={profile.vatNumber}
                    onChange={(e) => setProfile(p => ({ ...p, vatNumber: e.target.value }))}
                    placeholder="NL123456789B01"
                    className="h-12"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-[#666] mb-1 block">Beschrijving</label>
                  <textarea
                    value={profile.description}
                    onChange={(e) => setProfile(p => ({ ...p, description: e.target.value }))}
                    placeholder="Vertel iets over jezelf of je bedrijf..."
                    className="w-full p-3 border border-[#E5E5E5] rounded-xl text-sm min-h-[100px] resize-none focus:outline-none focus:ring-2 focus:ring-[#2E7D32]"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white font-bold rounded-xl"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" /> Opslaan</>}
            </Button>

            {/* Logout Button */}
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full h-12 border-[#C04A4A] text-[#C04A4A] hover:bg-[#C04A4A] hover:text-white font-bold rounded-xl"
            >
              <LogOut className="w-5 h-5 mr-2" /> Uitloggen
            </Button>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="space-y-6">
            {subscription?.status === 'active' ? (
              <>
                {/* Active Subscription Card */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-[#2E7D32]">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-[#2E7D32] rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Crown className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-bold text-[#2A2A2A]">Premium Actief</h2>
                      <p className="text-sm text-[#666] mt-1">
                        {subscription?.plan === 'weekly' ? '€4,99/week' : 
                         subscription?.plan === 'monthly' ? '€14,99/maand' : 
                         subscription?.plan === 'boost' ? 'Premium Boost' : 'Premium'}
                      </p>
                    </div>
                    <span className="px-3 py-1 bg-[#E8F5E9] text-[#2E7D32] text-sm font-bold rounded-full">
                      Actief
                    </span>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-[#E5E5E5] grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-[#9E9E9E]">Gestart op</p>
                      <p className="text-sm font-medium text-[#2A2A2A]">{formatDate(subscription?.startDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#9E9E9E]">Volgende facturatie</p>
                      <p className="text-sm font-medium text-[#2A2A2A]">{formatDate(subscription?.currentPeriodEnd)}</p>
                    </div>
                  </div>
                </div>

                {/* Cancel Subscription */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h3 className="font-bold text-[#2A2A2A] mb-2">Abonnement beëindigen</h3>
                  <p className="text-sm text-[#666] mb-4">
                    Je abonnement blijft actief tot het einde van de huidige periode. Daarna wordt het niet automatisch verlengd.
                  </p>
                  <Button
                    onClick={handleCancelSubscription}
                    variant="outline"
                    className="border-[#C04A4A] text-[#C04A4A] hover:bg-[#C04A4A] hover:text-white"
                  >
                    Abonnement beëindigen
                  </Button>
                </div>
              </>
            ) : (
              /* No Subscription Card */
              <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
                <div className="w-16 h-16 bg-[#F7F7F7] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-8 h-8 text-[#9E9E9E]" />
                </div>
                <h2 className="text-lg font-bold text-[#2A2A2A] mb-2">Geen actief abonnement</h2>
                <p className="text-sm text-[#666] mb-4">
                  Upgrade naar Premium voor onbeperkte woningen plaatsen en meer zichtbaarheid.
                </p>
                <Button
                  onClick={handleNavigateToPremium}
                  disabled={navigatingToPremium}
                  className="bg-[#2E7D32] hover:bg-[#256B2B] text-white font-bold"
                >
                  Bekijk Premium
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-[#2A2A2A] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#2E7D32]" />
                Facturen
              </h2>
              
              {loadingInvoices ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#2E7D32]" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-[#F7F7F7] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#9E9E9E]" />
                  </div>
                  <p className="text-[#666]">Nog geen facturen</p>
                  <p className="text-sm text-[#9E9E9E] mt-1">Facturen verschijnen hier na je eerste betaling</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invoices.map((invoice) => (
                    <div 
                      key={invoice.id}
                      className="flex items-center justify-between p-4 bg-[#F7F7F7] rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#2E7D32]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#2A2A2A] text-sm">{invoice.description || 'Premium Abonnement'}</p>
                          <p className="text-xs text-[#9E9E9E]">{formatDate(invoice.date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-[#2A2A2A]">{formatPrice(invoice.amount)}</span>
                        {invoice.pdfUrl && (
                          <a 
                            href={invoice.pdfUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-2 hover:bg-white rounded-lg transition-colors"
                          >
                            <Download className="w-5 h-5 text-[#2E7D32]" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-4 p-4 bg-[#FFF8E1] rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#2A2A2A]">Zakelijke facturen</p>
                  <p className="text-xs text-[#666] mt-1">
                    Vul je KVK- en BTW-nummer in bij je profiel om volledige zakelijke facturen te ontvangen die je kunt afschrijven op je belasting.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerhuurderAccountPage;
